import { SyntheticEvent } from "react"
import { Box, Tab, Tabs } from "@mui/material"
import { makeStyles } from "tss-react/mui"

export type FormTabConfig = Readonly<{
    label: string
    component: JSX.Element
    badge?: number
}>

type StandardFormTabsProps = Readonly<{
    tabs: ReadonlyArray<FormTabConfig>
    value: number
    onChange: (newValue: number) => void
}>

const useStyles = makeStyles()((theme) => ({
    tabBar: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    tab: {
        textTransform: "none",
    },
    tabLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
    },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.25rem",
        height: "1.25rem",
        padding: "0 .375rem",
        borderRadius: ".625rem",
        fontSize: ".75rem",
        fontWeight: 600,
        lineHeight: 1,
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[600],
    },
}))

/**
 * Underline-indicator tab strip for create/edit forms, plus the active tab's panel. This is the shared
 * version of the per-module form tabs (price list `FormTabs`, resources `RoomFormTabs`/`CarFormTabs`,
 * shops `TabForms`) — use it instead of the pill-style `StandardTabs`, which is for filters/segmented views.
 */
export function StandardFormTabs({ tabs, value, onChange }: StandardFormTabsProps) {
    const { classes } = useStyles()

    return (
        <>
            <Box className={classes.tabBar}>
                <Tabs value={value} onChange={(_event: SyntheticEvent, newValue: number) => onChange(newValue)}>
                    {tabs.map((tab, index) => (
                        <Tab
                            key={`${index}-${tab.label}`}
                            value={index}
                            label={
                                <span className={classes.tabLabel}>
                                    {tab.label}
                                    {tab.badge !== undefined && tab.badge > 0 && (
                                        <span className={classes.badge}>{tab.badge}</span>
                                    )}
                                </span>
                            }
                            className={classes.tab}
                            wrapped
                        />
                    ))}
                </Tabs>
            </Box>
            {tabs[value]?.component}
        </>
    )
}
